/**
 * EstimatesPanel (Phase D1.1-B · Feb 2026)
 * ----------------------------------------
 * Commercial Document Center — Estimates section.
 *
 * Features:
 *   • List with status filter (all / draft / sent / approved / expired / cancelled)
 *   • Global search (number, company, contact, phone, email, sales rep)
 *   • Create new Estimate
 *   • Row actions: Open (detail), PDF, Duplicate, WhatsApp (pre-filled wa.me), Cancel
 *   • RBAC: reps see only theirs; admins see all — enforced on the backend.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import EstimateForm from "@/components/EstimateForm";
import EstimateDetailModal from "@/components/EstimateDetailModal";
import InvoiceDetailModal from "@/components/InvoiceDetailModal";

const money = (v) =>
  `$${Number(v || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const STATUS_TONE = {
  draft: "bg-slate-100 text-slate-800 border-slate-300",
  sent: "bg-amber-100 text-amber-900 border-amber-300",
  approved: "bg-emerald-100 text-emerald-900 border-emerald-300",
  expired: "bg-neutral-200 text-neutral-600 border-neutral-300",
  cancelled: "bg-red-100 text-red-900 border-red-300",
};
const STATUS_LABEL_KEYS = {
  draft: "est_status_draft",
  sent: "est_status_sent",
  approved: "est_status_approved",
  expired: "est_status_expired",
  cancelled: "est_status_cancelled",
};

export default function EstimatesPanel() {
  const { t } = useI18n();
  const { isAdmin, can } = useAuth();
  const canViewAll =
    isAdmin || can("commercial", "view_all") || can("commercial", "estimates_all");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [settings, setSettings] = useState(null);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const [detailId, setDetailId] = useState(null);
  const [invoiceDetailId, setInvoiceDetailId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (status !== "all") params.status = status;
      if (q.trim()) params.q = q.trim();
      const { data } = await axios.get(`${API}/commercial/estimates`, {
        params,
        withCredentials: true,
      });
      setItems(data);
    } catch (e) {
      toast.error(t("est_panel_load_err"));
    } finally {
      setLoading(false);
    }
  }, [status, q]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/commercial/settings`, {
          withCredentials: true,
        });
        setSettings(data);
      } catch {
        /* non-blocking */
      }
    })();
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openNew = () => {
    setEditing(null);
    setFormOpen(true);
  };

  const openEdit = async (id) => {
    try {
      const { data } = await axios.get(`${API}/commercial/estimates/${id}`, {
        withCredentials: true,
      });
      setEditing(data);
      setFormOpen(true);
    } catch (e) {
      toast.error(t("est_panel_open_err"));
    }
  };

  const onSaved = () => {
    setFormOpen(false);
    setEditing(null);
    load();
  };

  const openDetail = (id) => setDetailId(id);

  const downloadPdf = async (est) => {
    // Feb 2026 — ventana síncrona en el clic (mismo patrón validado en
    // WhatsApp) para evitar el bloqueador de pop-ups tras el await.
    const w = window.open("", "_blank");
    try {
      const res = await axios.get(
        `${API}/commercial/estimates/${est.id}/pdf`,
        { responseType: "blob", withCredentials: true }
      );
      const url = URL.createObjectURL(res.data);
      if (w) w.location = url;
      else window.open(url, "_blank");
    } catch {
      if (w) w.close();
      toast.error(t("est_panel_pdf_err"));
    }
  };

  const duplicate = async (est) => {
    try {
      const { data } = await axios.post(
        `${API}/commercial/estimates/${est.id}/duplicate`,
        {},
        { withCredentials: true }
      );
      toast.success(t("est_panel_dup_ok", { n: data.number }));
      load();
    } catch {
      toast.error(t("est_panel_dup_err"));
    }
  };

  const waDigits = (est) => String(est.customer_phone || "").replace(/\D/g, "");

  // Feb 2026 — link público con token secreto (share_token). Se abre la
  // ventana de forma síncrona para evitar el bloqueador de popups.
  const openWhatsApp = async (est) => {
    const digits = waDigits(est);
    if (!digits) return;
    const w = window.open("", "_blank");
    try {
      const { data } = await axios.post(
        `${API}/commercial/estimates/${est.id}/share-token`,
        {},
        { withCredentials: true }
      );
      const url = `${window.location.origin}/?e=${data.token}`;
      const tpl =
        settings?.whatsapp_template ||
        "Hola {contact}, le compartimos la cotización {number} de {company}. {url}";
      let msg = tpl
        .replaceAll("{contact}", est.contact_person || "")
        .replaceAll("{company}", settings?.company_name || "")
        .replaceAll("{number}", est.number || "")
        .replaceAll("{url}", url);
      if (!msg.includes(url)) msg = `${msg}\n${url}`;
      const wa = `https://wa.me/${digits}?text=${encodeURIComponent(msg)}`;
      if (w) w.location = wa;
      else window.open(wa, "_blank");
    } catch {
      if (w) w.close();
      toast.error("No se pudo generar el enlace para compartir");
    }
  };

  const emailUrl = (est) => {
    if (!est.customer_email) return null;
    const subject = `Cotización ${est.number} — ${settings?.company_name || "Rawen's"}`;
    const body = `Hola ${est.contact_person || ""},\n\nAdjunto la cotización ${est.number}.\n\nGracias.`;
    return `mailto:${est.customer_email}?subject=${encodeURIComponent(
      subject
    )}&body=${encodeURIComponent(body)}`;
  };

  const filteredCount = items.length;

  return (
    <div data-testid="estimates-panel" className="space-y-6">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="label-overline">DOC / EST</div>
          <h1 className="font-display text-3xl sm:text-5xl font-black tracking-tighter">
            {t("est_panel_title")}
          </h1>
          <p className="text-sm text-[var(--text-secondary)] mt-1">
            {canViewAll ? t("est_panel_subtitle_all") : t("est_panel_subtitle_mine")}
            {" · "}
            {t("results_count", { n: filteredCount, s: filteredCount === 1 ? "" : "s" })}
          </p>
        </div>
        <button
          data-testid="estimates-new"
          onClick={openNew}
          className="font-display font-bold text-sm bg-black text-white px-5 py-3 hover:bg-[var(--accent-magenta,#E5007D)] transition-colors"
        >
          {t("est_panel_new_btn")}
        </button>
      </header>

      {/* Filters */}
      <section className="bg-white border border-[var(--border)] p-4 flex flex-wrap items-center gap-3">
        <div className="flex gap-1 overflow-x-auto">
          {["all", "draft", "sent", "approved", "expired", "cancelled"].map((k) => (
            <button
              key={k}
              data-testid={`estimates-filter-${k}`}
              onClick={() => setStatus(k)}
              className={`font-mono-tech text-xs px-3 py-1.5 border transition-colors whitespace-nowrap ${
                status === k
                  ? "bg-black text-white border-black"
                  : "border-[var(--border)] hover:bg-black hover:text-white"
              }`}
            >
              {k === "all" ? t("all") : t(STATUS_LABEL_KEYS[k])}
            </button>
          ))}
        </div>
        <input
          data-testid="estimates-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t("est_panel_search_placeholder")}
          className="flex-1 min-w-[240px] border border-[var(--border)] px-3 py-2 text-sm"
        />
      </section>

      {/* Table */}
      <section className="bg-white border border-[var(--border)] overflow-x-auto">
        <table className="min-w-full">
          <thead className="bg-[var(--bg)]">
            <tr>
              <Th>#</Th>
              <Th>{t("est_panel_col_client")}</Th>
              <Th>{t("est_panel_col_contact")}</Th>
              <Th>{t("est_panel_col_status")}</Th>
              <Th>{t("est_panel_col_office")}</Th>
              <Th>{t("est_panel_col_sales_rep")}</Th>
              <Th className="text-right">{t("est_panel_col_total")}</Th>
              <Th>{t("est_panel_col_created")}</Th>
              <Th>{t("est_panel_col_expires")}</Th>
              <Th />
            </tr>
          </thead>
          <tbody className="divide-y divide-[var(--border)]">
            {loading && (
              <tr>
                <td colSpan={10} className="p-6 text-center text-sm text-[var(--text-secondary)]">
                  {t("loading_ellipsis")}
                </td>
              </tr>
            )}
            {!loading && items.length === 0 && (
              <tr>
                <td colSpan={10} className="p-6 text-center text-sm text-[var(--text-secondary)]">
                  {t("est_panel_empty")}
                </td>
              </tr>
            )}
            {!loading &&
              items.map((e) => (
                <tr
                  key={e.id}
                  data-testid={`estimates-row-${e.number}`}
                  className="hover:bg-[var(--bg)] transition-colors"
                >
                  <Td>
                    <button
                      onClick={() => openDetail(e.id)}
                      className="font-mono-tech text-xs font-bold underline decoration-dotted"
                      data-testid={`estimates-open-${e.number}`}
                    >
                      {e.number}
                    </button>
                  </Td>
                  <Td>
                    <div className="text-sm font-bold">{e.company}</div>
                  </Td>
                  <Td>
                    <div className="text-xs text-[var(--text-secondary)]">
                      {e.contact_person}
                      {e.customer_phone ? ` · ${e.customer_phone}` : ""}
                    </div>
                  </Td>
                  <Td>
                    <span
                      className={`font-mono-tech text-[0.65rem] font-bold tracking-wider px-2 py-1 border ${
                        STATUS_TONE[e.status] || "bg-neutral-100"
                      }`}
                    >
                      {STATUS_LABEL_KEYS[e.status] ? t(STATUS_LABEL_KEYS[e.status]) : e.status}
                    </span>
                  </Td>
                  <Td>
                    <div className="text-xs">{e.office_name || e.office_key}</div>
                  </Td>
                  <Td>
                    <div className="text-xs">{e.sales_rep_name}</div>
                  </Td>
                  <Td className="text-right font-mono-tech text-sm">
                    {money(e.total_amount)}
                  </Td>
                  <Td>
                    <div className="text-xs">{String(e.created_at || "").slice(0, 10)}</div>
                  </Td>
                  <Td>
                    <div className="text-xs">{String(e.expires_at || "").slice(0, 10)}</div>
                  </Td>
                  <Td>
                    <div className="flex items-center gap-2 justify-end">
                      <ActionBtn
                        testid={`estimates-pdf-${e.number}`}
                        onClick={() => downloadPdf(e)}
                        title={t("action_pdf")}
                      >
                        PDF
                      </ActionBtn>
                      {emailUrl(e) && (
                        <ActionBtn
                          as="a"
                          href={emailUrl(e)}
                          target="_blank"
                          rel="noreferrer"
                          testid={`estimates-email-${e.number}`}
                          title={t("est_panel_action_email_title")}
                        >
                          ✉
                        </ActionBtn>
                      )}
                      {!!waDigits(e) && (
                        <ActionBtn
                          onClick={() => openWhatsApp(e)}
                          testid={`estimates-wa-${e.number}`}
                          title={t("est_panel_action_wa_title")}
                        >
                          WA
                        </ActionBtn>
                      )}
                      <ActionBtn
                        onClick={() => duplicate(e)}
                        testid={`estimates-dup-${e.number}`}
                        title={t("est_panel_action_duplicate_title")}
                      >
                        ⧉
                      </ActionBtn>
                    </div>
                  </Td>
                </tr>
              ))}
          </tbody>
        </table>
      </section>

      {/* Form modal */}
      {formOpen && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start justify-center p-4 overflow-auto">
          <div className="bg-white w-full max-w-5xl border border-black shadow-2xl">
            <EstimateForm
              initial={editing}
              onCancel={() => {
                setFormOpen(false);
                setEditing(null);
              }}
              onSaved={onSaved}
            />
          </div>
        </div>
      )}

      {/* Detail modal */}
      {detailId && (
        <EstimateDetailModal
          estimateId={detailId}
          onClose={() => setDetailId(null)}
          onChanged={() => {
            load();
          }}
          onEdit={(est) => {
            setDetailId(null);
            openEdit(est.id);
          }}
          onOpenInvoice={(invId) => setInvoiceDetailId(invId)}
          settings={settings}
        />
      )}

      {invoiceDetailId && (
        <InvoiceDetailModal
          invoiceId={invoiceDetailId}
          onClose={() => setInvoiceDetailId(null)}
          onChanged={load}
          settings={settings}
        />
      )}
    </div>
  );
}

function Th({ children, className = "" }) {
  return (
    <th
      className={`px-3 py-2 text-left font-mono-tech text-[0.65rem] font-bold tracking-[0.2em] text-[var(--text-secondary)] ${className}`}
    >
      {children}
    </th>
  );
}
function Td({ children, className = "" }) {
  return <td className={`px-3 py-2 ${className}`}>{children}</td>;
}
function ActionBtn({ children, testid, onClick, as, href, target, rel, title }) {
  const cls =
    "font-mono-tech text-[0.65rem] font-bold border border-black px-2 py-1 hover:bg-black hover:text-white transition-colors";
  if (as === "a") {
    return (
      <a data-testid={testid} href={href} target={target} rel={rel} title={title} className={cls}>
        {children}
      </a>
    );
  }
  return (
    <button data-testid={testid} onClick={onClick} title={title} className={cls}>
      {children}
    </button>
  );
}
